import { t as tCem } from '../utils/i18n'
/**
 * DetectionsView — Feed agregado de todas las detecciones de los sitios.
 *
 * Bloques:
 *  1. Cards de totales: vehículos (por tipo), personas (por dirección), LPR.
 *  2. Tabla por cámara con qué aporta cada una.
 *  3. Feed cronológico de events individuales (paginación por before_ts).
 *
 * Polling:
 *  - Counts (top + tabla): cada 5s
 *  - Feed: cada 8s + opción "pausar auto-refresh" para revisar sin que se
 *    mueva (cuando el operador está leyendo un evento concreto).
 *
 * Nota: por ahora hardcodea sitio = primer sitio del tenant. Cuando haya >1
 *       EDS, agregamos un selector de sitio en el header.
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { useAuthStore } from '../store/authStore'

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmtAgo(iso) {
  if (!iso) return '—'
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000)
  if (diff < 0)     return 'ahora'
  if (diff < 5)     return 'ahora'
  if (diff < 60)    return `ago ${diff}s`
  if (diff < 3600)  return `ago ${Math.floor(diff/60)}m`
  if (diff < 86400) return `ago ${Math.floor(diff/3600)}h`
  return `ago ${Math.floor(diff/86400)}d`
}

function fmtTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

const TYPE_META = {
  vehicle_summary  : { icon: '🚗', label: 'Vehículo (cerrado)', color: '#3b82f6' },
  person_summary   : { icon: '🚶', label: 'Persona (cerrada)',  color: '#a855f7' },
  vehicle_detected : { icon: '🚙', label: 'Vehículo en frame',  color: '#10b981' },
  person_detected  : { icon: '🧍', label: 'Persona en frame',   color: '#10b981' },
  lpr              : { icon: '🪪', label: 'Matrícula',          color: '#f59e0b' },
}

const DIR_ARROW = { right: '→', left: '←', east: 'E', west: 'O', idle: '⊝' }

// ─── pequeños bloques visuales ────────────────────────────────────────────────

function Card({ title, total, sub, color, icon }) {
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '14px 16px', minWidth: 0,
      display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 11, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600,
      }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        {title}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1, marginTop: 4 }}>
        {total}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          {sub}
        </div>
      )}
    </div>
  )
}

function MiniDir({ value, color }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 2,
      fontSize: 11, fontWeight: 700, color: value > 0 ? color : 'var(--text-muted)',
      opacity: value > 0 ? 1 : 0.5, marginRight: 6,
    }}>{value}</span>
  )
}

// ─── tabla por cámara ─────────────────────────────────────────────────────────

function CameraTable({ items }) {
  if (!items || items.length === 0) {
    return (
      <div style={{
        padding: '20px', textAlign: 'center', color: 'var(--text-muted)',
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 8, fontSize: 12,
      }}>
        {tCem('Sin eventos en la ventana seleccionada','No events in selected window')}
      </div>
    )
  }
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 8, overflow: 'hidden',
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--bg-root)' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left',  fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tCem('Cámara','Camera')}</th>
            <th style={{ padding: '8px 8px',  textAlign: 'right', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>🚗</th>
            <th style={{ padding: '8px 8px',  textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>→ ← ⊝</th>
            <th style={{ padding: '8px 8px',  textAlign: 'right', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>🚶</th>
            <th style={{ padding: '8px 8px',  textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>E O ⊝</th>
            <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tCem('Última','Latest')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map(it => {
            const v = it.vehicles, p = it.persons
            const types = Object.entries(v.by_type)
              .map(([t,n]) => `${t}·${n}`).join(' ')
            return (
              <tr key={it.camera_name} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '8px 12px' }}>
                  <div style={{ fontWeight: 600 }}>{it.camera_name}</div>
                  {types && (
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                      {types}
                    </div>
                  )}
                </td>
                <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, fontSize: 14 }}>{v.total}</td>
                <td style={{ padding: '8px 8px', textAlign: 'center', fontSize: 11 }}>
                  <MiniDir value={v.by_dir.right} color="#10b981" />
                  <MiniDir value={v.by_dir.left}  color="#3b82f6" />
                  <MiniDir value={v.by_dir.idle}  color="#6b7280" />
                </td>
                <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, fontSize: 14 }}>{p.total}</td>
                <td style={{ padding: '8px 8px', textAlign: 'center', fontSize: 11 }}>
                  <MiniDir value={p.by_dir.east} color="#10b981" />
                  <MiniDir value={p.by_dir.west} color="#a855f7" />
                  <MiniDir value={p.by_dir.idle} color="#6b7280" />
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 10, color: 'var(--text-muted)' }}>
                  {fmtAgo(it.last_event_at)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── feed cronológico ─────────────────────────────────────────────────────────

function DetectionRow({ d }) {
  const meta = TYPE_META[d.event_type] || { icon: '◆', label: d.event_type, color: 'var(--text-muted)' }
  const p    = d.payload || {}
  const dir  = p.direction
  const dur  = p.duration_s
  const dx   = p.dx_px
  const vtype = p.vehicle_type
  const tid   = p.track_id

  let detail = ''
  if (d.event_type === 'vehicle_summary') {
    const parts = []
    if (vtype) parts.push(vtype)
    if (dir && dir !== 'idle') parts.push(`${DIR_ARROW[dir] || dir}${Math.round(Math.abs(dx||0))}px`)
    else parts.push('sin desplazamiento')
    if (dur) parts.push(`${dur.toFixed(1)}s`)
    detail = parts.join(' · ')
  } else if (d.event_type === 'person_summary') {
    const parts = []
    if (dir === 'east') parts.push('hacia este (frente)')
    else if (dir === 'west') parts.push('hacia oeste (espaldas)')
    else parts.push('sin desplazamiento')
    if (dur) parts.push(`${dur.toFixed(1)}s`)
    detail = parts.join(' · ')
  } else if (d.event_type === 'vehicle_detected') {
    detail = `${p.label || 'vehículo'} entrando${tid ? ` · #${tid}` : ''}`
  } else if (d.event_type === 'person_detected') {
    detail = `person detected${tid ? ` · #${tid}` : ''}`
  } else if (d.event_type === 'lpr') {
    detail = d.plate ? `placa: ${d.plate}` : 'placa detectada'
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '80px 24px minmax(100px, 1fr) minmax(120px, 1.5fr) 80px',
      gap: 10, alignItems: 'center',
      padding: '6px 12px', borderTop: '1px solid var(--border)',
      fontSize: 12,
    }}>
      <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace' }}>
        {fmtTime(d.ts)}
      </span>
      <span style={{ fontSize: 14 }}>{meta.icon}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {d.camera_name}
      </span>
      <span style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {detail}
      </span>
      <span style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' }}>
        {fmtAgo(d.ts)}
      </span>
    </div>
  )
}

// ─── main view ────────────────────────────────────────────────────────────────

export default function DetectionsView() {
  const token = useAuthStore(s => s.token)

  const [sites, setSites] = useState([])
  const [siteId, setSiteId] = useState(null)
  const [windowMin, setWindowMin] = useState(60)
  const [typeFilter, setTypeFilter] = useState('all')
  const [cameraFilter, setCameraFilter] = useState('')
  const [paused, setPaused] = useState(false)

  const [counts, setCounts] = useState(null)
  const [byCamera, setByCamera] = useState([])
  const [detections, setDetections] = useState([])

  const [, setTick] = useState(0)
  const lastFetch = useRef(0)

  // Cargar sitios
  useEffect(() => {
    fetch('/api/sites/status', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(arr => {
        setSites(arr)
        if (arr[0]?.id) setSiteId(arr[0].id)
      })
  }, [token])

  // Refs para evitar requests en cascada:
  //  - abortRef: cancela los fetches anteriores cuando llega uno nuevo
  //  - inFlightRef: marca "una carga en curso" para que el interval no la dispare 2x
  const abortRef = useRef(null)
  const inFlightRef = useRef(false)

  const loadAll = useCallback(async () => {
    if (!siteId || !token) return
    if (inFlightRef.current) return    // ya hay una carga corriendo, saltar
    // Abortar lo anterior si por algún motivo no completó
    if (abortRef.current) abortRef.current.abort()
    const ctl = new AbortController()
    abortRef.current = ctl
    inFlightRef.current = true
    lastFetch.current = Date.now()

    const auth = { headers: { Authorization: `Bearer ${token}` }, signal: ctl.signal }
    const qsBase = `site_id=${siteId}&window_minutes=${windowMin}`
    const qsFeed = `${qsBase}&limit=200&event_type=${typeFilter}` +
      (cameraFilter ? `&camera_name=${encodeURIComponent(cameraFilter)}` : '')

    try {
      const [rC, rB, rF] = await Promise.all([
        fetch(`/api/analytics/counts?${qsBase}`, auth),
        fetch(`/api/analytics/counts/by-camera?${qsBase}`, auth),
        fetch(`/api/analytics/detections?${qsFeed}`, auth),
      ])
      if (rC.ok) setCounts(await rC.json())
      if (rB.ok) {
        const j = await rB.json()
        setByCamera(j.items || [])
      }
      if (rF.ok) {
        const j = await rF.json()
        setDetections(j.items || [])
      }
    } catch (e) {
      // AbortError esperado cuando cancelamos; ignorar
      if (e?.name !== 'AbortError') console.error('DetectionsView fetch error:', e)
    } finally {
      inFlightRef.current = false
    }
  }, [siteId, windowMin, typeFilter, cameraFilter, token])

  // Una sola fuente: carga al cambiar deps + interval cuando NO está paused.
  // Antes había DOS useEffect distintos que reiniciaban el setInterval cada
  // vez que cambiaba loadAll → causaba múltiples loops y requests
  // sobrepuestas. Ahora un solo effect: dispara una vez + setInterval.
  useEffect(() => {
    loadAll()
    if (paused) return
    const id = setInterval(loadAll, 5000)
    return () => {
      clearInterval(id)
      if (abortRef.current) abortRef.current.abort()
    }
  }, [loadAll, paused])
  useEffect(() => {
    const id = setInterval(() => setTick(t => t+1), 1000)
    return () => clearInterval(id)
  }, [])

  const ageS = Math.max(0, Math.floor((Date.now() - lastFetch.current) / 1000))

  const vehicleSub = counts ? (() => {
    const t = counts.vehicles.by_type
    const dirs = counts.vehicles.by_dir
    const parts = []
    Object.entries(t).forEach(([k,v]) => parts.push(`${k} ${v}`))
    const dirStr = `→${dirs.right}  ←${dirs.left}  ⊝${dirs.idle}`
    return (
      <>
        <div>{parts.join(' · ') || '—'}</div>
        <div style={{ marginTop: 2 }}>{dirStr}</div>
      </>
    )
  })() : null

  const personSub = counts ? (() => {
    const dirs = counts.persons.by_dir
    return (
      <div>
        {tCem('este','east')} {dirs.east} · {tCem('oeste','west')} {dirs.west} · idle {dirs.idle}
      </div>
    )
  })() : null

  const lprCount = detections.filter(d => d.event_type === 'lpr').length

  return (
    <div style={{ padding: 16, overflow: 'auto', height: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        marginBottom: 16,
      }}>
        {(() => { const _en = typeof window!=='undefined'&&window.location.hostname==='cad.retaliq.net'; return (
        <><div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{_en ? 'Detections' : 'Detecciones'}</h2>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            {_en ? 'Live aggregated events · refresh every 5s ·' : 'Eventos agregados en vivo · refresh cada 5s ·'}{' '}
            <span style={{ color: paused ? '#f59e0b' : '#10b981' }}>
              {paused ? (_en ? 'paused' : 'pausado') : (_en ? `updated ${ageS}s ago` : `actualizado ago ${ageS}s`)}
            </span>
          </div>
        </div>
        <select
          value={siteId || ''}
          onChange={e => setSiteId(Number(e.target.value))}
          style={selectStyle}
        >
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={windowMin} onChange={e => setWindowMin(Number(e.target.value))} style={selectStyle}>
          <option value={15}>15 min</option>
          <option value={60}>1 h</option>
          <option value={240}>4 h</option>
          <option value={1440}>24 h</option>
          <option value={10080}>{_en ? '7 days' : '7 días'}</option>
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={selectStyle}>
          <option value="all">{_en ? 'All types' : 'Todos los tipos'}</option>
          <option value="vehicle_summary">{_en ? 'Vehicles (closed)' : 'Vehículos (cerrados)'}</option>
          <option value="vehicle_detected">{_en ? 'Vehicles (in frame)' : 'Vehículos (en frame)'}</option>
          <option value="person_summary">{_en ? 'Persons (closed)' : 'Personas (cerradas)'}</option>
          <option value="person_detected">{_en ? 'Persons (in frame)' : 'Personas (en frame)'}</option>
          <option value="lpr">{_en ? 'ANPR' : 'LPR'}</option>
        </select>
        <select value={cameraFilter} onChange={e => setCameraFilter(e.target.value)} style={selectStyle}>
          <option value="">{_en ? 'All cameras' : 'Todas las cámaras'}</option>
          {byCamera.map(c => <option key={c.camera_name} value={c.camera_name}>{c.camera_name}</option>)}
        </select>
        <button
          onClick={() => setPaused(p => !p)}
          style={{
            padding: '5px 10px', fontSize: 11, fontWeight: 600,
            background: paused ? '#f59e0b' : 'var(--bg-surface)',
            color: paused ? '#fff' : 'var(--text-secondary)',
            border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
          }}
        >{paused ? (_en ? '▶ Resume' : '▶ Reanudar') : (_en ? '⏸ Pause' : '⏸ Pausar')}</button></>
        )})()}
      </div>

      {/* Cards de totales */}
      <div style={{
        display: 'grid', gap: 10,
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        marginBottom: 16,
      }}>
        <Card title={tCem('Vehículos','Vehicles')} icon="🚗"
              total={counts?.vehicles.total ?? '—'}
              color="#3b82f6"
              sub={vehicleSub} />
        <Card title={tCem('Personas','Persons')} icon="🚶"
              total={counts?.persons.total ?? '—'}
              color="#a855f7"
              sub={personSub} />
        <Card title="LPR" icon="🪪"
              total={lprCount}
              color="#f59e0b"
              sub={tCem('placas detectadas en feed actual','plates detected in current feed')} />
        <Card title={tCem('En frame ahora','In frame now')} icon="●"
              total={(counts?.active_now.vehicles ?? 0) + (counts?.active_now.persons ?? 0)}
              color="#10b981"
              sub={`🚗 ${counts?.active_now.vehicles ?? 0}  ·  🚶 ${counts?.active_now.persons ?? 0}`} />
      </div>

      {/* Tabla por cámara */}
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {tCem('Por cámara','By camera')}
        </h3>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {tCem('últimos','last')} {windowMin >= 60 ? `${windowMin/60}h` : `${windowMin}min`}
        </span>
      </div>
      <div style={{ marginBottom: 16 }}>
        <CameraTable items={byCamera} />
      </div>

      {/* Feed cronológico */}
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {tCem('Feed cronológico','Chronological feed')}
        </h3>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {detections.length} events
        </span>
      </div>

      <div style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 8, overflow: 'hidden',
      }}>
        {/* Cabecera de columnas */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '80px 24px minmax(100px, 1fr) minmax(120px, 1.5fr) 80px',
          gap: 10, padding: '8px 12px',
          background: 'var(--bg-root)',
          fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          <span>{tCem('Hora','Time')}</span>
          <span></span>
          <span>{tCem('Cámara','Camera')}</span>
          <span>{tCem('Detalle','Detail')}</span>
          <span style={{ textAlign: 'right' }}>{tCem('Edad','Age')}</span>
        </div>
        {detections.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
            {tCem('Sin eventos','No events')}
          </div>
        ) : (
          detections.map(d => <DetectionRow key={d.id} d={d} />)
        )}
      </div>
    </div>
  )
}

const selectStyle = {
  fontSize: 11, padding: '4px 8px', borderRadius: 4,
  background: 'var(--bg-surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)',
}
